import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Map, { Marker, MapRef, NavigationControl } from 'react-map-gl/mapbox';
import { MAPBOX_TOKEN } from 'config/Mapbox';
import DistributionHubService from 'service/DistributionHubService';
import { DistributionHubCreateReq } from 'domain/payload/request/distributionHub/DistributionHubCreateReq';
import { DistributionHubUpdateReq } from 'domain/payload/request/distributionHub/DistributionHubUpdateReq';
import { DistributionHubMoreAccessUserListConfigResp } from 'domain/payload/response/distributionHub/DistributionHubMoreAccessUserListConfig';
import CustomDropdown from 'component/CustomDropdown';

const DistributionHubForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = Boolean(id && id !== '0');
    const navigate = useNavigate();

    const { profile } = useSelector((state: any) => state.auth);
    const token = profile?.data?.token;

    const distributionHubService = useMemo(() => DistributionHubService(), []);
    const mapRef = useRef<MapRef | null>(null);

    // Form states - Basic Information
    const [name, setName] = useState('');
    const [region, setRegion] = useState('');
    const [address, setAddress] = useState('');

    // Form states - Person in Charge (PIC)
    const [isMoreAccess, setIsMoreAccess] = useState(false);
    const [moreAccessUsers, setMoreAccessUsers] = useState<DistributionHubMoreAccessUserListConfigResp[]>([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [picName, setPicName] = useState('');
    const [picPhone, setPicPhone] = useState('');
    const [picEmail, setPicEmail] = useState('');

    // Form states - Location & Coordinates
    const [latitude, setLatitude] = useState<number>(-6.2915);
    const [longitude, setLongitude] = useState<number>(106.8122);
    const [areaId, setAreaId] = useState<string>('');
    const [province, setProvince] = useState('');
    const [city, setCity] = useState('');
    const [district, setDistrict] = useState('');
    const [subdistrict, setSubdistrict] = useState('');

    // Loading states
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isLoadingArea, setIsLoadingArea] = useState(false);
    const [isLoadingSave, setIsLoadingSave] = useState(false);

    // Fetch area config based on lat/long
    const fetchAreaByCoordinates = useCallback(async (lat: number, lng: number) => {
        if (!token) return;
        setIsLoadingArea(true);
        try {
            const res = await distributionHubService.distributionHubAreaConfig(token, lat, lng);
            const areaData = Array.isArray(res.data) ? res.data[0] : res.data;
            if (areaData) {
                setAreaId(areaData.areaId || '');
                setProvince(areaData.province || '');
                setCity(areaData.city || '');
                setDistrict(areaData.district || '');
                setSubdistrict(areaData.subdistrict || '');
            }
        } catch (error) {
            console.error('Failed to fetch area config by coordinates:', error);
        } finally {
            setIsLoadingArea(false);
        }
    }, [token, distributionHubService]);

    // Initial data fetch (More Access user list + Detail if edit mode)
    useEffect(() => {
        if (!token) return;

        let isMounted = true;
        const initData = async () => {
            setIsLoadingInitial(true);
            try {
                // Fetch More Access users for dropdown
                const usersRes = await distributionHubService.distributionHubMoreAccessUserListConfig(token);
                if (isMounted) {
                    setMoreAccessUsers(usersRes.data ?? []);
                }

                // If editing existing hub, fetch detail
                if (isEditMode && id) {
                    const detailRes = await distributionHubService.distributionHubDetail(token, id);
                    const hub = detailRes.data;
                    if (hub && isMounted) {
                        setName(hub.name || '');
                        setAreaId(hub.areaId || '');
                        setRegion(hub.region || '');
                        setAddress(hub.address || '');
                        setPicName(hub.picName || '');
                        setPicEmail(hub.picEmail || '');
                        setPicPhone(hub.picPhone || '');
                        setIsMoreAccess(hub.moreAccessAffiliated ?? false);
                        if (!hub.moreAccessAffiliated) {
                            setManualPicData({
                                picName: hub.picName || '',
                                picPhone: hub.picPhone || '',
                                picEmail: hub.picEmail || '',
                            });
                        }

                        const hubLat = hub.latitude || -6.2915;
                        const hubLng = hub.longitude || 106.8122;
                        setLatitude(hubLat);
                        setLongitude(hubLng);

                        setProvince(hub.province || '');
                        setCity(hub.city || '');
                        setDistrict(hub.district || '');
                        setSubdistrict(hub.subdistrict || '');

                        // Match user dropdown if More Access
                        if (hub.moreAccessAffiliated && usersRes.data) {
                            const matchedUser = usersRes.data.find(
                                (u: any) => u.email?.toLowerCase() === hub.picEmail?.toLowerCase()
                            );
                            if (matchedUser) {
                                setSelectedUserId(matchedUser.id);
                            }
                        }

                        // Center map on existing location
                        mapRef.current?.flyTo({
                            center: [hubLng, hubLat],
                            zoom: 13,
                            duration: 500,
                        });
                    }
                } else if (isMounted) {
                    // Default coordinate area fetch on create
                    fetchAreaByCoordinates(-6.2915, 106.8122);
                }
            } catch (error: any) {
                console.error('Error loading initial data:', error);
                const errMsg = error.response?.data?.message || error.message || 'Gagal mengambil data';
                alert(`Error: ${errMsg}`);
            } finally {
                if (isMounted) {
                    setIsLoadingInitial(false);
                }
            }
        };

        initData();

        return () => {
            isMounted = false;
        };
    }, [token, id, isEditMode, distributionHubService, fetchAreaByCoordinates]);

    // Manual PIC backup state for non-affiliated mode
    const [manualPicData, setManualPicData] = useState({
        picName: '',
        picPhone: '',
        picEmail: '',
    });

    // Options formatted for CustomDropdown
    const moreAccessUserOptions = useMemo(() => {
        return moreAccessUsers.map((u) => ({
            id: u.id,
            name: u.email ? `${u.name} (${u.email})` : u.name,
            description: u.phone ? `Phone: ${u.phone}` : undefined,
        }));
    }, [moreAccessUsers]);

    // Handle More Access user selection
    const handleSelectUser = (userId: string) => {
        setSelectedUserId(userId);
        const selected = moreAccessUsers.find((u) => u.id === userId);
        if (selected) {
            setPicName(selected.name || '');
            setPicEmail(selected.email || '');
            setPicPhone(selected.phone || '');
        } else {
            setPicName('');
            setPicEmail('');
            setPicPhone('');
        }
    };

    // Toggle More Access switch
    const handleToggleMoreAccess = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setIsMoreAccess(checked);
        if (!checked) {
            // Restore previous manual PIC data
            setPicName(manualPicData.picName);
            setPicPhone(manualPicData.picPhone);
            setPicEmail(manualPicData.picEmail);
        } else {
            // Save current manual PIC data before switching to More Access
            setManualPicData({
                picName,
                picPhone,
                picEmail,
            });

            // Auto-fill from selected user or first user in list
            let targetUser = moreAccessUsers.find((u) => u.id === selectedUserId);
            if (!targetUser && moreAccessUsers.length > 0) {
                targetUser = moreAccessUsers[0];
                setSelectedUserId(targetUser.id);
            }

            if (targetUser) {
                setPicName(targetUser.name || '');
                setPicEmail(targetUser.email || '');
                setPicPhone(targetUser.phone || '');
            }
        }
    };

    // Manual input change handlers
    const handlePicNameChange = (val: string) => {
        setPicName(val);
        if (!isMoreAccess) {
            setManualPicData((prev) => ({ ...prev, picName: val }));
        }
    };

    const handlePicPhoneChange = (val: string) => {
        setPicPhone(val);
        if (!isMoreAccess) {
            setManualPicData((prev) => ({ ...prev, picPhone: val }));
        }
    };

    const handlePicEmailChange = (val: string) => {
        setPicEmail(val);
        if (!isMoreAccess) {
            setManualPicData((prev) => ({ ...prev, picEmail: val }));
        }
    };

    // Handle map click
    const handleMapClick = (e: any) => {
        const newLng = Number(e.lngLat.lng);
        const newLat = Number(e.lngLat.lat);
        setLongitude(newLng);
        setLatitude(newLat);
        fetchAreaByCoordinates(newLat, newLng);
    };

    // Handle marker drag end
    const handleMarkerDragEnd = (e: any) => {
        const newLng = Number(e.lngLat.lng);
        const newLat = Number(e.lngLat.lat);
        setLongitude(newLng);
        setLatitude(newLat);
        fetchAreaByCoordinates(newLat, newLng);
    };

    // Handle Form Submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            alert('Hub Name wajib diisi');
            return;
        }
        if (!region.trim()) {
            alert('Region wajib diisi');
            return;
        }

        if (!token) {
            alert('Token tidak ditemukan. Silakan login kembali.');
            return;
        }

        setIsLoadingSave(true);
        try {
            if (isEditMode && id) {
                const req: DistributionHubUpdateReq = {
                    id,
                    name: name.trim(),
                    region: region.trim(),
                    areaId: areaId || '',
                    address: address.trim(),
                    picName: picName.trim(),
                    picEmail: picEmail.trim(),
                    picPhone: picPhone.trim(),
                    userId: isMoreAccess ? selectedUserId : '',
                    latitude,
                    longitude,
                };
                await distributionHubService.distributionHubUpdate(token, req);
                alert(`Distribution Hub "${name}" berhasil diperbarui!`);
            } else {
                const req: DistributionHubCreateReq = {
                    name: name.trim(),
                    region: region.trim(),
                    areaId: areaId || '',
                    address: address.trim(),
                    picName: picName.trim(),
                    picEmail: picEmail.trim(),
                    picPhone: picPhone.trim(),
                    userId: isMoreAccess ? selectedUserId : '',
                    latitude,
                    longitude,
                };
                await distributionHubService.distributionHubCreate(token, req);
                alert(`Distribution Hub "${name}" berhasil dibuat!`);
            }
            navigate('/distribution-hub');
        } catch (error: any) {
            console.error('Error saving distribution hub:', error);
            const errMsg = error.response?.data?.message || error.message || 'Terjadi kesalahan saat menyimpan data';
            alert(`Error: ${errMsg}`);
        } finally {
            setIsLoadingSave(false);
        }
    };

    if (isLoadingInitial) {
        return (
            <div className="page-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <form onSubmit={handleSubmit}>
                {/* ══ Top Title ══ */}
                <div className="mb-4">
                    <h5 className="fw-bold mb-0 text-dark">
                        {isEditMode ? 'Edit Distribution Hub' : 'Create Distribution Hub'}
                    </h5>
                </div>

                <div className="row g-4">
                    {/* ══════════════════════════════════════
                        LEFT COLUMN: Basic Info & PIC
                    ══════════════════════════════════════ */}
                    <div className="col-12 col-lg-6 d-flex flex-column gap-4">
                        {/* ── Card 1: Basic Information ── */}
                        <div className="card border-0 shadow-sm rounded-3 p-4 bg-white">
                            <div className="d-flex align-items-center mb-3">
                                <i className="fa fa-cubes text-primary me-2" style={{ fontSize: '16px' }}></i>
                                <h6 className="fw-bold mb-0 text-dark">Basic Information</h6>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                    Hub Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Distribution Hub Jakarta Barat"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    style={{ fontSize: '13.5px' }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                    Region <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Jabodetabek"
                                    value={region}
                                    onChange={(e) => setRegion(e.target.value)}
                                    required
                                    style={{ fontSize: '13.5px' }}
                                />
                            </div>

                            <div>
                                <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                    Address
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={4}
                                    placeholder="Kawasan Pergudangan Daan Mogot KM 12 No. 8"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    style={{ fontSize: '13.5px', resize: 'vertical' }}
                                />
                            </div>
                        </div>

                        {/* ── Card 2: Person in Charge (PIC) ── */}
                        <div className="card border-0 shadow-sm rounded-3 p-4 bg-white">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <i className="fa fa-user text-primary me-2" style={{ fontSize: '15px' }}></i>
                                    <h6 className="fw-bold mb-0 text-dark">Person in Charge (PIC)</h6>
                                </div>
                                {isMoreAccess && (
                                    <span
                                        className="badge rounded-pill d-inline-flex align-items-center px-2.5 py-1"
                                        style={{
                                            backgroundColor: '#dcfce7',
                                            color: '#15803d',
                                            border: '1px solid #bbf7d0',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            letterSpacing: '0.3px',
                                        }}
                                    >
                                        <i className="fa fa-check-circle text-success me-1" style={{ fontSize: '11px' }}></i>
                                        MORE ACCESS
                                    </span>
                                )}
                            </div>

                            {/* Mint Toggle Callout Box */}
                            <div
                                className="d-flex justify-content-between align-items-center p-3 mb-3 rounded-3"
                                style={{
                                    backgroundColor: isMoreAccess ? '#f0fdf4' : '#f8fafc',
                                    border: `1px solid ${isMoreAccess ? '#bbf7d0' : '#e2e8f0'}`,
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <div>
                                    <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                        Select from More Access
                                    </div>
                                    <div className="text-muted small" style={{ fontSize: '11.5px' }}>
                                        Fields below are auto-filled and locked
                                    </div>
                                </div>
                                <div className="form-check form-switch m-0 p-0 d-flex align-items-center">
                                    <input
                                        className="form-check-input ms-0"
                                        type="checkbox"
                                        role="switch"
                                        checked={isMoreAccess}
                                        onChange={handleToggleMoreAccess}
                                        style={{ width: '2.5em', height: '1.3em', cursor: 'pointer' }}
                                    />
                                </div>
                            </div>

                            {/* Dropdown Select Account (Visible only when More Access is ON) */}
                            {isMoreAccess && (
                                <div className="mb-3">
                                    <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                        Select Account
                                    </label>
                                    <div className="w-100">
                                        <CustomDropdown
                                            value={selectedUserId}
                                            options={moreAccessUserOptions}
                                            onChange={(opt) => handleSelectUser(opt.id ? String(opt.id) : '')}
                                            placeholder="-- Pilih Akun More Access --"
                                            searchable={moreAccessUsers.length > 5}
                                            searchPlaceholder="Cari user More Access..."
                                            containerClassName="w-100"
                                            buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* PIC Name */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                    PIC Name
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${isMoreAccess ? 'bg-light text-muted' : ''}`}
                                    placeholder="Dian Kusuma Wardani"
                                    value={picName}
                                    onChange={(e) => handlePicNameChange(e.target.value)}
                                    disabled={isMoreAccess}
                                    style={{ fontSize: '13.5px' }}
                                />
                            </div>

                            {/* Phone & Email row */}
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                        Phone Number
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${isMoreAccess ? 'bg-light text-muted' : ''}`}
                                        placeholder="+62 813-1122-8901"
                                        value={picPhone}
                                        onChange={(e) => handlePicPhoneChange(e.target.value)}
                                        disabled={isMoreAccess}
                                        style={{ fontSize: '13.5px' }}
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className={`form-control ${isMoreAccess ? 'bg-light text-muted' : ''}`}
                                        placeholder="dian.kusuma@moreaccess.id"
                                        value={picEmail}
                                        onChange={(e) => handlePicEmailChange(e.target.value)}
                                        disabled={isMoreAccess}
                                        style={{ fontSize: '13.5px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════
                        RIGHT COLUMN: Location & Coordinates
                    ══════════════════════════════════════ */}
                    <div className="col-12 col-lg-6">
                        <div className="card border-0 shadow-sm rounded-3 p-4 bg-white h-100">
                            <div className="d-flex align-items-center mb-3">
                                <i className="fa fa-map-marker text-primary me-2" style={{ fontSize: '16px' }}></i>
                                <h6 className="fw-bold mb-0 text-dark">Location & Coordinates</h6>
                            </div>

                            {/* Coordinates Input (Read-only) */}
                            <div className="mb-3">
                                <label className="form-label text-muted small mb-1" style={{ fontSize: '12px' }}>
                                    Coordinates (Latitude, Longitude)
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white text-muted">
                                        <i className="fa fa-crosshairs" style={{ fontSize: '13px' }}></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control bg-light text-dark fw-medium"
                                        disabled
                                        value={`${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}`}
                                        style={{ fontSize: '13px' }}
                                    />
                                </div>
                            </div>

                            {/* Drag Marker Helper Banner */}
                            <div
                                className="d-flex justify-content-between align-items-center p-2 px-3 mb-2 rounded border"
                                style={{ backgroundColor: '#f0f9ff', borderColor: '#bae6fd' }}
                            >
                                <span className="text-primary small fw-medium" style={{ fontSize: '12px' }}>
                                    <i className="fa fa-arrows text-primary me-2"></i>
                                    Drag map camera to position the center marker
                                </span>
                                <span
                                    className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 fw-normal"
                                    style={{ fontSize: '11px' }}
                                >
                                    {Number(latitude).toFixed(4)}, {Number(longitude).toFixed(4)}
                                </span>
                            </div>

                            {/* Mapbox Interactive Map */}
                            <div
                                className="position-relative w-100 rounded-3 overflow-hidden border mb-3"
                                style={{ height: '280px', borderColor: '#e2e8f0' }}
                            >
                                {isLoadingArea && (
                                    <div
                                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.65)', zIndex: 10 }}
                                    >
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="text-muted small mt-1" style={{ fontSize: '11px' }}>
                                            Mendeteksi wilayah...
                                        </span>
                                    </div>
                                )}

                                <Map
                                    ref={mapRef}
                                    mapboxAccessToken={MAPBOX_TOKEN}
                                    initialViewState={{
                                        latitude,
                                        longitude,
                                        zoom: 13,
                                    }}
                                    style={{ width: '100%', height: '100%' }}
                                    mapStyle="mapbox://styles/mapbox/streets-v12"
                                    onClick={handleMapClick}
                                >
                                    <NavigationControl position="bottom-right" />
                                    <Marker
                                        latitude={latitude}
                                        longitude={longitude}
                                        anchor="bottom"
                                        draggable
                                        onDragEnd={handleMarkerDragEnd}
                                    >
                                        <div
                                            className="d-flex flex-column align-items-center"
                                            style={{ cursor: 'grab', transform: 'translateY(-6px)' }}
                                        >
                                            {/* Pin Circle */}
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center shadow"
                                                style={{
                                                    width: '34px',
                                                    height: '34px',
                                                    backgroundColor: '#2B75DA',
                                                    border: '3px solid #ffffff',
                                                    color: '#ffffff',
                                                }}
                                                title="Geser atau klik untuk mengubah lokasi"
                                            >
                                                <i className="fa fa-cubes" style={{ fontSize: '13px' }}></i>
                                            </div>
                                        </div>
                                    </Marker>
                                </Map>
                            </div>

                            {/* Administrative Area (Read-only) */}
                            <div className="mt-2">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="d-flex align-items-center">
                                        <i className="fa fa-check-circle text-success me-2" style={{ fontSize: '14px' }}></i>
                                        <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>
                                            Administrative Area
                                        </span>
                                    </div>
                                    <span className="text-muted small" style={{ fontSize: '11px' }}>
                                        Auto-detected &amp; Read-only
                                    </span>
                                </div>

                                <div className="row g-2 mb-2">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label text-muted small mb-1" style={{ fontSize: '11.5px' }}>
                                            Province
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light text-muted"
                                            disabled
                                            value={province}
                                            placeholder="Province"
                                            style={{ fontSize: '13px' }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label text-muted small mb-1" style={{ fontSize: '11.5px' }}>
                                            City / Kota
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light text-muted"
                                            disabled
                                            value={city}
                                            placeholder="City / Kota"
                                            style={{ fontSize: '13px' }}
                                        />
                                    </div>
                                </div>

                                <div className="row g-2">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label text-muted small mb-1" style={{ fontSize: '11.5px' }}>
                                            District / Kecamatan
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light text-muted"
                                            disabled
                                            value={district}
                                            placeholder="District / Kecamatan"
                                            style={{ fontSize: '13px' }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label text-muted small mb-1" style={{ fontSize: '11.5px' }}>
                                            Subdistrict / Kelurahan
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control bg-light text-muted"
                                            disabled
                                            value={subdistrict}
                                            placeholder="Subdistrict / Kelurahan"
                                            style={{ fontSize: '13px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ══ Bottom Actions ══ */}
                <div className="d-flex justify-content-end align-items-center gap-2 mt-4 pt-3 border-top">
                    <button
                        type="button"
                        className="btn btn-outline-secondary px-4"
                        onClick={() => navigate('/distribution-hub')}
                        disabled={isLoadingSave}
                        style={{ fontSize: '13px' }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary px-4 d-inline-flex align-items-center text-white"
                        disabled={isLoadingSave}
                        style={{ fontSize: '13px' }}
                    >
                        {isLoadingSave ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                        ) : (
                            <i className="fa fa-check me-2" style={{ fontSize: '13px' }}></i>
                        )}
                        {isEditMode ? 'Update Hub' : 'Create Hub'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default DistributionHubForm;
